import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "@/pages/Login";
import AgencyDashboard from "@/pages/AgencyDashboard";
import HRDashboard from "@/pages/HRDashboard";
import VerificationTeamDashboard from "@/pages/VerificationTeamDashboard";
import QCDashboard from "@/pages/QCDashboard";
import EmployeePortal from "@/pages/EmployeePortal";
import DataEntryDashboard from "@/pages/DataEntryDashboard";
import InhouseVerificationDashboard from "@/pages/InhouseVerificationDashboard";
import VendorManagementDashboard from "@/pages/VendorManagementDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import OperationsManagerDashboard from "@/pages/OperationsManagerDashboard";
import AgentInternalVendorDashboard from "@/pages/AgentInternalVendorDashboard";
import FieldVerificationApp from "@/pages/FieldVerificationApp";
import FieldVerificationPage from "@/pages/FieldVerificationPage";
import B2BDashboard from "@/pages/B2BDashboard";
import B2BFieldVerification from "@/pages/B2BFieldVerification";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminMigrationsPage from "@/pages/AdminMigrationsPage";
import ExternalIntegrationPage from "@/pages/ExternalIntegrationPage";
import FeatureShowcase from "@/pages/FeatureShowcase";
import SmartChat from "@/components/chat/SmartChat";
import InstantHelper from "@/components/shared/InstantHelper";
import { Toaster } from "@/components/ui/sonner";
import { Eye, ArrowLeft } from "lucide-react";
import { useIdleLogout } from "@/hooks/useIdleLogout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.baseURL = API;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Master Manager impersonation state
  const [masterSession, setMasterSession] = useState(null); // Stores original master session
  const [viewingAs, setViewingAs] = useState(null); // Current impersonated user

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedMasterSession = localStorage.getItem('masterSession');
    
    // Restore master session if exists
    if (savedMasterSession) {
      setMasterSession(JSON.parse(savedMasterSession));
    }
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
      
      // Check if we're in "viewing as" mode (master session exists but current user is different)
      const savedMasterSession = localStorage.getItem('masterSession');
      if (savedMasterSession) {
        const master = JSON.parse(savedMasterSession);
        if (response.data.email !== master.user.email) {
          setViewingAs(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('masterSession');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    setViewingAs(null);
    setMasterSession(null);
    localStorage.removeItem('masterSession');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('masterSession');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setMasterSession(null);
    setViewingAs(null);
  };

  // Auto-logout after 30 min of inactivity (security + clean session
  // hygiene for shared workstations). Disabled while logged out so the
  // login page can stay open indefinitely.
  useIdleLogout(!!user, handleLogout);

  // Master Manager "View As" functionality
  const handleViewAs = (token, targetUser) => {
    // Save master session if not already saved (first time viewing as)
    if (!masterSession && user?.email === 'master@veriflow.com') {
      const currentToken = localStorage.getItem('token');
      const session = { token: currentToken, user: user };
      setMasterSession(session);
      localStorage.setItem('masterSession', JSON.stringify(session));
    }
    
    // Switch to target user
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(targetUser);
    setViewingAs(targetUser);
  };

  // Return to Master Manager
  const handleReturnToMaster = () => {
    if (masterSession) {
      localStorage.setItem('token', masterSession.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${masterSession.token}`;
      setUser(masterSession.user);
      setViewingAs(null);
      // Keep masterSession for next view-as
    }
  };

  // Check if current user is Master Manager (for showing View As option)
  const isMasterManager = user?.email === 'master@veriflow.com' || masterSession?.user?.email === 'master@veriflow.com';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  const getDashboardByRole = () => {
    if (!user) return <Navigate to="/login" replace />;
    
    // Pass view-as props to dashboards
    const commonProps = {
      user,
      onLogout: handleLogout,
      onSwitchAccount: handleLogin,
      onViewAs: isMasterManager ? handleViewAs : null,
      isViewingAs: !!viewingAs,
      onReturnToMaster: handleReturnToMaster,
      masterSession
    };
    
    switch (user.role) {
      case 'agency_admin':
        return <AgencyDashboard {...commonProps} />;
      case 'b2b_admin':
        // B2B Portal Admin - redirect to B2B Portal
        return <Navigate to="/b2b" replace />;
      case 'hr_admin':
        return <HRDashboard {...commonProps} />;
      case 'qc':
      case 'qc_operations':
        return <DataEntryDashboard {...commonProps} />;
      case 'data_entry_team':
        return <DataEntryDashboard {...commonProps} />;
      case 'inhouse_verification':
        return <InhouseVerificationDashboard {...commonProps} />;
      case 'vendor_management':
        // Agent Vendor Team (in-house) - uses VendorManagementDashboard for vendor oversight
        return <VendorManagementDashboard {...commonProps} />;
      case 'verification_team':
        // Route based on team_type for verification_team role
        if (user.team_type === 'vendor_management' || user.team_type === 'vendor') {
          return <VendorManagementDashboard {...commonProps} />;
        }
        return <InhouseVerificationDashboard {...commonProps} />;
      case 'operations_manager':
        return <OperationsManagerDashboard {...commonProps} />;
      case 'agent_vendor':
        // Legacy agent_vendor role - use InhouseVerificationDashboard (same as Internal Agent)
        return <InhouseVerificationDashboard {...commonProps} />;
      case 'vendor':
      case 'external_vendor':
        // External vendors (like Veecheck) use VendorManagementDashboard with Field Agent features
        return <VendorManagementDashboard {...commonProps} />;
      case 'employee':
        return <EmployeePortal {...commonProps} />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <div className="App">
      <Toaster position="top-right" />
      
      {/* Master Manager "Viewing As" Banner */}
      {viewingAs && masterSession && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 flex items-center justify-between shadow-lg"
          data-testid="viewing-as-banner"
        >
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5" />
            <span className="font-medium">
              Demo Mode: Viewing as <span className="font-bold">{viewingAs.name}</span> ({viewingAs.role})
            </span>
          </div>
          <button
            onClick={handleReturnToMaster}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-1.5 rounded-lg transition-colors font-medium"
            data-testid="return-to-master-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to Master Manager
          </button>
        </div>
      )}
      
      <BrowserRouter>
        <div className={viewingAs ? 'pt-12' : ''}>
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />} />
            <Route path="/features" element={<FeatureShowcase />} />
            <Route path="/field-verification/:caseId/:checkId" element={<FieldVerificationApp />} />
            <Route path="/field-verification/:checkId" element={<FieldVerificationPage />} />
            <Route path="/agent-internal-vendor" element={
              <ProtectedRoute allowedRoles={['verification_team', 'inhouse_verification', 'agency_admin', 'operations_manager']}>
                <AgentInternalVendorDashboard user={user} onLogout={handleLogout} onSwitchAccount={handleLogin} />
              </ProtectedRoute>
            } />
            <Route path="/b2b" element={
              <ProtectedRoute allowedRoles={['agency_admin', 'qc', 'qc_operations', 'operations_manager', 'b2b_admin']}>
                <B2BDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            } />
            {/* B2B Field Verification - Public route for field agents */}
            <Route path="/b2b-field/:caseId" element={<B2BFieldVerification />} />
            <Route path="/admin/settings" element={
              <ProtectedRoute allowedRoles={['agency_admin']}>
                <AdminSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/integrations" element={
              <ProtectedRoute allowedRoles={['agency_admin']}>
                <ExternalIntegrationPage user={user} />
              </ProtectedRoute>
            } />
            <Route path="/admin/migrations" element={
              <ProtectedRoute allowedRoles={['agency_admin']}>
                <AdminMigrationsPage />
              </ProtectedRoute>
            } />
            <Route path="/" element={getDashboardByRole()} />
          </Routes>
        </div>
      </BrowserRouter>
      {/* Smart Chat - visible when logged in */}
      {user && <SmartChat user={user} />}
      {/* Instant Helper - visible when logged in */}
      {user && <InstantHelper />}
    </div>
  );
}

export default App;